import {SelectField, KeyTextField, LinkField, isFilled} from '@prismicio/client'
import Link from 'next/link'
import {useLinkResolver} from 'prismicio'
import {Button} from 'src/common/Button'
import {EVENTS, track, TrackingProps} from 'src/common/helpers'
import {IconType, Icon} from 'src/common/Icon'

export const ContentSection = ({
  highlight_1_icon,
  highlight_2_icon,
  highlight_1_title,
  highlight_1_description,
  highlight_2_title,
  highlight_2_description,
  primary_button_label,
  primary_button_url,
  enableReskin,
  index,
  pageUid,
  componentIndex,
}: {
  highlight_1_icon: SelectField<'Up arrow' | 'Down arrow' | 'Plus bold'>
  highlight_2_icon: SelectField<'Up arrow' | 'Down arrow' | 'Plus bold'>
  highlight_1_title: KeyTextField
  highlight_1_description: KeyTextField
  highlight_2_title: KeyTextField
  highlight_2_description: KeyTextField
  primary_button_label: KeyTextField
  primary_button_url: LinkField
  enableReskin?: boolean
  index?: number
  pageUid?: string
  componentIndex?: number
}) => {
  const {linkResolver} = useLinkResolver()
  const icon1 = highlight_1_icon?.toLowerCase().replace(/ /g, '-') as IconType
  const icon2 = highlight_2_icon?.toLowerCase().replace(/ /g, '-') as IconType

  return (
    <div className="flex flex-col gap-6 md:gap-10">
      <div className="flex gap-12 max-sm:pb-2">
        {isFilled.keyText(highlight_1_title) && (
          <div className="flex flex-col">
            <div className="flex items-center gap-1">
              <Icon name={icon1} className="h-3 w-3 text-neon-450" />
              <p className="text-body-l text-moss-white">{highlight_1_title}</p>
            </div>
            <span className={`text-20 leading-6 ${enableReskin ? 'text-moss-white/[70%]' : 'text-grey-135'}`}>
              {highlight_1_description}
            </span>
          </div>
        )}
        {isFilled.keyText(highlight_2_title) && (
          <div className="flex flex-col">
            <div className="flex items-center gap-1">
              <Icon name={icon2} className="h-3 w-3 text-neon-450" />
              <p className="text-body-l text-moss-white">{highlight_2_title}</p>
            </div>
            <span className={`text-20 leading-6 ${enableReskin ? 'text-moss-white/[70%]' : 'text-grey-135'}`}>
              {highlight_2_description}
            </span>
          </div>
        )}
      </div>
      {isFilled.link(primary_button_url) && (
        <ButtonVariant
          href={linkResolver(primary_button_url, 'primary-button-content-section')}
          {...(primary_button_url.link_type === 'Document'
            ? {
                locale: primary_button_url.lang || 'en-us',
              }
            : {})}
          trackingProps={{
            event: EVENTS.CLICK,
            mktgId: `customer-stories-${index}`,
            location: `component-${componentIndex}-customer-stories`,
            pageName: pageUid,
            dataType: 'customer-stories-button',
          }}
          label={primary_button_label}
          enableReskin={!!enableReskin}
        />
      )}
    </div>
  )
}

const ButtonVariant = ({
  enableReskin,
  href,
  locale,
  trackingProps,
  label,
}: {
  enableReskin: boolean
  href: Record<string, any>
  locale?: string
  trackingProps: TrackingProps
  label: KeyTextField
}) => {
  if (enableReskin) {
    return (
      <Link
        href={href}
        {...(locale ? {locale} : {})}
        className="group relative flex h-[38px] w-fit items-center gap-[14px] rounded-[4px] border border-moss-grey-150 bg-moss-white pl-[14px] pr-[7px] hover:bg-moss-grey-100"
        onClick={() =>
          track({
            event: EVENTS.CLICK,
            mktgId: trackingProps.mktgId,
            location: trackingProps.location,
            pageName: trackingProps.pageName,
            dataType: trackingProps.dataType,
          })
        }
      >
        <span className="text-button-s uppercase text-black">{label}</span>
        <div className="flex h-6 w-6 items-center justify-center rounded-sm bg-green-neon group-hover:bg-green-neon/[80%]">
          <Icon name="arrow" className="h-2 w-2.5 text-green-dark" />
        </div>
      </Link>
    )
  } else {
    return (
      <Button variant="fillV2" className="w-fit" href={href} locale={locale}>
        {label}
      </Button>
    )
  }
}
